// ═══════════════════════════════════════
// ATLAS BEASTS — CREATURE BONDING UI
// Task 14: Select a character, bond with creature, watch level build
// ═══════════════════════════════════════

import { useState, useEffect } from 'react'
import Modal from '../ui/Modal'
import { LAUNCH_CHARACTERS } from '../../data/characters'
import { ELEMENTS } from '../../types'
import type { Creature, Character } from '../../types'

type BondPhase = 'select' | 'bonding' | 'result'

interface BondingModalProps {
  creature: Creature | null
  onClose: () => void
  onBondComplete?: (creature: Creature, character: Character, newBondLevel: number) => void
}

const BOND_GAIN_PER_SESSION = 8 // bond XP per bonding session

export default function BondingModal({ creature, onClose, onBondComplete }: BondingModalProps) {
  const [phase, setPhase] = useState<BondPhase>('select')
  const [selectedChar, setSelectedChar] = useState<Character | null>(null)
  const [progress, setProgress] = useState(0)

  // Reset on open
  useEffect(() => {
    if (creature) {
      setPhase('select')
      setSelectedChar(null)
      setProgress(0)
    }
  }, [creature])

  function startBonding() {
    if (!selectedChar) return
    setPhase('bonding')
    setProgress(0)

    // Animate progress bar over 2.5s
    const duration = 2500
    const start = performance.now()
    const tick = (now: number) => {
      const pct = Math.min((now - start) / duration, 1)
      setProgress(Math.round(pct * 100))
      if (pct < 1) {
        requestAnimationFrame(tick)
      } else {
        setPhase('result')
      }
    }
    requestAnimationFrame(tick)
  }

  function handleDone() {
    if (creature && selectedChar) {
      const newBondLevel = Math.min((creature.bondLevel || 0) + BOND_GAIN_PER_SESSION, 100)
      onBondComplete?.(creature, selectedChar, newBondLevel)
    }
    onClose()
  }

  if (!creature) return null

  const el = ELEMENTS[creature.element]
  const currentBond = creature.bondLevel ?? 0
  const projectedBond = Math.min(currentBond + BOND_GAIN_PER_SESSION, 100)
  const isSigPair = selectedChar?.id === creature.bondedCharacterId

  // Filter: only unbonded or same-bonded characters (no stealing another creature's bond)
  const eligibleChars = LAUNCH_CHARACTERS.filter(c =>
    !c.bondedCreatureId || c.bondedCreatureId === creature.id
  )

  return (
    <Modal
      open={!!creature}
      onClose={phase === 'bonding' ? undefined! : onClose}
      persistent={phase === 'bonding'}
      size="md"
      title={phase === 'select' ? 'Bond with Crew' : phase === 'bonding' ? 'Bonding...' : 'Bond Complete'}
    >
      {/* ── Phase 1: Character Select ── */}
      {phase === 'select' && (
        <div className="space-y-5">
          {/* Creature header */}
          <div
            className="flex items-center gap-3 p-3 rounded-ab border border-white border-opacity-8"
            style={{ background: `linear-gradient(135deg, ${el.color}15, transparent)` }}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `radial-gradient(circle, ${el.color}30, #080E1A)` }}
            >
              <span className="text-2xl">{el.emoji}</span>
            </div>
            <div>
              <p className="font-display font-black text-base text-ab-text">{creature.name}</p>
              <p className="text-body text-xs italic opacity-70">{creature.species}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-label text-xs">Current Bond</p>
              <p className="font-display font-black text-sm" style={{ color: el.color }}>
                {currentBond} / 100
              </p>
            </div>
          </div>

          <p className="text-label text-xs">Choose a crew member to bond with</p>

          {eligibleChars.length === 0 ? (
            <p className="text-body text-sm opacity-60 text-center py-4">
              All crew members are bonded to other creatures.
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
              {eligibleChars.map(char => {
                const charEl = ELEMENTS[char.element]
                const isSig = char.id === creature.bondedCharacterId
                const isSelected = selectedChar?.id === char.id

                return (
                  <button
                    key={char.id}
                    onClick={() => setSelectedChar(char)}
                    className={`w-full text-left p-3 rounded-ab border transition-all duration-150 ${
                      isSelected
                        ? 'bg-ab-surface2'
                        : 'bg-ab-surface border-white border-opacity-5 hover:border-opacity-15'
                    }`}
                    style={isSelected ? { borderColor: charEl.color } : {}}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{charEl.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-display font-bold text-sm text-ab-text">
                            {char.name}
                          </p>
                          {isSig && (
                            <span
                              className="font-display font-bold text-xs px-1.5 py-0.5 rounded-ab"
                              style={{ color: el.color, background: `${el.color}20` }}
                            >
                              Sig Bond
                            </span>
                          )}
                          {isSelected && (
                            <span className="ml-auto font-display font-bold text-xs" style={{ color: charEl.color }}>✓</span>
                          )}
                        </div>
                        <p className="text-body text-xs opacity-60 capitalize">
                          {char.faction.replace('-', ' ')} · {char.class}
                        </p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {selectedChar && (
            <div
              className="p-3 rounded-ab border border-white border-opacity-8 text-xs"
              style={{ background: `${el.color}10` }}
            >
              <p className="text-label mb-1">Bond preview</p>
              <div className="flex items-center justify-between">
                <span className="text-body">
                  {creature.name} + {selectedChar.name}
                </span>
                <span className="font-display font-bold" style={{ color: el.color }}>
                  {currentBond} → {projectedBond}
                </span>
              </div>
              {isSigPair && (
                <p className="text-ab-earth text-xs mt-1 font-display font-bold">
                  ✦ Signature pair — +2 bonus bond XP
                </p>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button
              onClick={startBonding}
              disabled={!selectedChar}
              className={`btn-primary flex-1 ${!selectedChar ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              Start Bond →
            </button>
          </div>
        </div>
      )}

      {/* ── Phase 2: Bonding Animation ── */}
      {phase === 'bonding' && selectedChar && (
        <div className="space-y-6 text-center py-4">
          {/* Creatures orbiting each other */}
          <div className="flex items-center justify-center gap-6">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${el.color}30, #080E1A)`,
                boxShadow: `0 0 20px ${el.color}40`,
                animation: 'pulse 1s ease-in-out infinite',
              }}
            >
              <span className="text-3xl">{el.emoji}</span>
            </div>

            <div className="flex flex-col items-center gap-1">
              <div className="flex gap-1">
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: el.color,
                      opacity: progress > i * 33 ? 1 : 0.2,
                      transition: 'opacity 0.3s',
                    }}
                  />
                ))}
              </div>
              <p className="text-ab-gold font-display font-black text-xs">BOND</p>
            </div>

            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${ELEMENTS[selectedChar.element].color}30, #080E1A)`,
                boxShadow: `0 0 20px ${ELEMENTS[selectedChar.element].color}40`,
                animation: 'pulse 1s ease-in-out infinite 0.5s',
              }}
            >
              <span className="text-3xl">{ELEMENTS[selectedChar.element].emoji}</span>
            </div>
          </div>

          <div>
            <p className="font-display font-black text-base text-ab-text mb-1">
              {creature.name} × {selectedChar.name}
            </p>
            <p className="text-body text-xs opacity-60">Forming bond connection...</p>
          </div>

          {/* Progress bar */}
          <div>
            <div className="stat-bar mb-2">
              <div
                className="stat-bar-fill transition-none"
                style={{
                  width: `${progress}%`,
                  background: `linear-gradient(90deg, ${el.color}80, ${el.color})`,
                  boxShadow: `0 0 8px ${el.color}60`,
                  transition: 'width 0.1s linear',
                }}
              />
            </div>
            <p className="font-display font-bold text-xs" style={{ color: el.color }}>
              {progress}%
            </p>
          </div>
        </div>
      )}

      {/* ── Phase 3: Result ── */}
      {phase === 'result' && selectedChar && (
        <div className="space-y-5 text-center py-2">
          <div>
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                background: `radial-gradient(circle, ${el.color}25, #080E1A)`,
                boxShadow: `0 0 40px ${el.color}30`,
              }}
            >
              <span className="text-4xl">🐾</span>
            </div>
            <h3 className="font-display font-black text-xl text-ab-text mb-1">Bond Formed</h3>
            <p className="text-body text-sm opacity-70">
              {creature.name} and {selectedChar.name} are now bonded
            </p>
          </div>

          {/* Bond level result */}
          <div className="card-base p-4">
            <div className="flex justify-between items-center mb-2">
              <p className="text-label text-xs">Bond Level</p>
              <div className="flex items-center gap-2">
                <span className="text-ab-text-dim text-xs font-display font-bold">{currentBond}</span>
                <span className="text-ab-text-dim text-xs">→</span>
                <span className="font-display font-black text-sm" style={{ color: el.color }}>
                  {projectedBond}
                </span>
              </div>
            </div>
            <div className="stat-bar">
              <div
                className="stat-bar-fill"
                style={{
                  width: `${projectedBond}%`,
                  background: `linear-gradient(90deg, ${el.color}60, ${el.color})`,
                  boxShadow: `0 0 8px ${el.color}40`,
                }}
              />
            </div>
          </div>

          {/* Sig pair bonus */}
          {isSigPair && (
            <div
              className="p-3 rounded-ab border text-sm font-display font-bold"
              style={{ color: '#39FF14', borderColor: '#39FF1440', background: '#39FF1410' }}
            >
              ✦ Signature Bond — Synergy bonus active in battle
            </div>
          )}

          {projectedBond === 100 && (
            <div
              className="p-3 rounded-ab border text-sm font-display font-bold"
              style={{ color: el.color, borderColor: `${el.color}40`, background: `${el.color}10` }}
            >
              ⚡ Max Bond! Evolution path unlocked.
            </div>
          )}

          <button onClick={handleDone} className="btn-primary w-full">
            Done ✓
          </button>
        </div>
      )}
    </Modal>
  )
}
